// FeedbackDetailPage.tsx — one feedback item + its discussion, route /feedback/:id.
//
// Publicly readable. Everyone sees the user view (header/tags/description/
// attachments/activity/comments + side rail Status/Roadmap/Details cards).
// A viewer with item.canManage additionally sees inline edit affordances on
// the SAME page (no separate admin route) — status/priority/assign/pin are
// further staff-only, matching the backend's can_manage vs is_staff split
// (see backend/apps/feedback/permissions.py). "Preview public view" flips
// canManage off locally so a contributor/staff member can see exactly what a
// normal visitor sees without actually leaving edit mode.
import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Avatar,
  Button,
  IconButton,
  Panel,
  SectionLabel,
  Switch,
  TextField,
  Textarea,
  useToast,
} from '@/ui';
import { Icon } from '@/icons';
import { GuestBanner } from '@/components/GuestBanner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSession, useRequireAuth } from '@/hooks';
import { useFeedbackItem } from '@/hooks/useFeedback';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { ApiError } from '@/core/lib/apiClient';
import type {
  FeedbackAttachment,
  FeedbackPriority,
  FeedbackStage,
  FeedbackStageState,
  FeedbackStatus,
} from '@/core/models/feedback';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_PLATFORM_LABELS,
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_LABELS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_STATUSES,
} from '@/core/models/feedback';
import {
  CATEGORY_ICONS,
  FEEDBACK_TOPIC_FIELDS,
  MAX_TAGS_PER_ITEM,
  TAG_MAX_LEN,
  attachmentUploadErrorMessage,
  describeManageError,
  displayFeedbackId,
  fieldValueLabel,
  formatFileSize,
  parseFeedbackIdInput,
  relativeTime,
} from './feedbackMeta';
import { StatusBadge } from './StatusBadge';
import { FeedbackNotFound } from './FeedbackNotFound';
import { FeedbackHowItWorksPanel } from './panels/FeedbackHowItWorksPanel';
import { FeedbackMarkdown } from './lib/markdown';
import { MarkdownEditor } from './MarkdownEditor';
import { DEVICE_SNAPSHOT_FIELD_LABELS, DEVICE_SNAPSHOT_FIELD_ORDER } from './lib/deviceSnapshot';

// ── error state (distinct from FeedbackNotFound — see the 404-vs-other rule) ─

const ErrorWrap = styled.div`
  padding: 40px 20px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const ErrorTitle = styled.h1`
  margin: 0 0 6px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ErrorBlurb = styled.p`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/** A genuine load failure (network/5xx) — distinct from FeedbackNotFound
 * (404). Never rendered for "does not exist"; always offers a retry rather
 * than silently claiming the item is gone. */
function FeedbackLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <ErrorWrap role="alert">
      <ErrorTitle>Could not load this feedback</ErrorTitle>
      <ErrorBlurb>Something went wrong loading this item. Please try again.</ErrorBlurb>
      <Button variant="ghost" onClick={onRetry}>
        Try again
      </Button>
    </ErrorWrap>
  );
}

// ── shared layout primitives ────────────────────────────────────────────────

const Card = styled.article`
  padding: 18px 20px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const Upvote = styled.button<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 58px;
  padding: 10px 8px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Caret = styled.span`
  display: inline-flex;
  transform: rotate(180deg);
`;

const Count = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const HeadMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeadTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const CategoryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const WatchButton = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  padding: 7px 14px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

const Tag = styled.span<{ $tone?: 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme, $tone }) =>
    $tone === 'danger' ? theme.color.danger : theme.color.textPrimary};
  background: ${({ theme, $tone }) =>
    $tone === 'danger' ? `${theme.color.danger}22` : 'transparent'};
  border: 1px solid
    ${({ theme, $tone }) => ($tone === 'danger' ? 'transparent' : theme.color.glassBorder)};
`;

const TagRemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const AddTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const AddTagRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AddTagInput = styled.input`
  width: 140px;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DescriptionBlock = styled.div`
  margin-top: 16px;
`;

const StepsList = styled.ol`
  margin: 12px 0 0;
  padding-left: 20px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.6;
`;

const ExpectedActual = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const EAColumn = styled.div<{ $tone: 'expected' | 'actual' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme, $tone }) =>
    $tone === 'expected' ? `${theme.color.success}22` : `${theme.color.danger}22`};
`;

const EAHead = styled.div<{ $tone: 'expected' | 'actual' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  color: ${({ theme, $tone }) => ($tone === 'expected' ? theme.color.success : theme.color.danger)};
`;

const EAText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MetaGrid = styled.dl`
  margin: 14px 0 0;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px 16px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaTerm = styled.dt`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textMuted};
`;

const MetaValue = styled.dd`
  margin: 0;
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

/** Small standalone caption for a description-area sub-block (Additional
 * context, Steps to reproduce) — NOT a <dt> (unlike MetaTerm), since these
 * blocks aren't inside a <dl>. */
const InlineLabel = styled.h3`
  margin: 0 0 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textMuted};
`;

const ContextBlock = styled.div`
  margin-top: 12px;
`;

const Section = styled.section`
  margin-top: 18px;
`;

const SubHead = styled.h2`
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Empty = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ── attachments ──────────────────────────────────────────────────────────────

const AttachmentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const AttachmentCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 110px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const Thumb = styled.img`
  width: 100%;
  height: 70px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const FileIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 70px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textMuted};
`;

const AttachmentName = styled.a`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentSize = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: 10px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const AttachmentDownload = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.brandFrom};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const AttachmentRemove = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.danger};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const UploadButton = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
`;

const IMAGE_TYPE_RE = /^image\//;

function AttachmentsSection({
  attachments,
  canUpload,
  canDeleteAll,
  myHandle,
  onUpload,
  onRemove,
}: {
  attachments: FeedbackAttachment[];
  canUpload: boolean;
  canDeleteAll: boolean;
  myHandle: string | undefined;
  onUpload: (file: File) => Promise<void>;
  onRemove: (id: number) => Promise<{ refreshed: boolean }>;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<FeedbackAttachment | null>(null);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || busy) return;
    setBusy(true);
    try {
      await onUpload(file);
    } catch (err) {
      toast(attachmentUploadErrorMessage(err, 'Could not upload that file. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      const { refreshed } = await onRemove(target.id);
      if (!refreshed) {
        toast('Attachment removed, but the page may be out of date. Refresh to confirm.');
      }
    } catch (err) {
      toast(describeManageError(err, 'Could not remove that attachment. Please try again.'));
    }
  }

  if (attachments.length === 0 && !canUpload) return null;

  return (
    <Section>
      <SubHead>
        Attachments {attachments.length > 0 && `(${attachments.length})`}
        {canUpload && attachments.length < 5 && (
          <UploadButton $disabled={busy}>
            <Icon name="upload" size={13} /> {busy ? 'Uploading…' : 'Add file'}
            <input
              type="file"
              disabled={busy}
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,application/pdf,text/plain"
              onChange={(e) => void handleFile(e)}
            />
          </UploadButton>
        )}
      </SubHead>
      {attachments.length === 0 ? (
        <Empty>No attachments.</Empty>
      ) : (
        <AttachmentGrid>
          {attachments.map((a) => {
            const canDeleteThis = canDeleteAll || a.uploader.handle === myHandle;
            return (
              <AttachmentCard key={a.id}>
                {canDeleteThis && (
                  <AttachmentRemove
                    type="button"
                    aria-label={`Remove ${a.filename}`}
                    onClick={() => setPendingDelete(a)}
                  >
                    <Icon name="close" size={12} />
                  </AttachmentRemove>
                )}
                {IMAGE_TYPE_RE.test(a.content_type) ? (
                  <Thumb src={a.url} alt={a.filename} />
                ) : (
                  <FileIconBox>
                    <Icon name="file-text" size={22} />
                  </FileIconBox>
                )}
                <AttachmentName href={a.url} target="_blank" rel="noreferrer">
                  {a.filename}
                </AttachmentName>
                <AttachmentSize>{formatFileSize(a.size)}</AttachmentSize>
                <AttachmentDownload href={a.url} download={a.filename}>
                  <Icon name="download" size={11} /> Download
                </AttachmentDownload>
              </AttachmentCard>
            );
          })}
        </AttachmentGrid>
      )}
      {pendingDelete && (
        <ConfirmDialog
          title="Remove attachment"
          message={`Remove "${pendingDelete.filename}"? This can't be undone.`}
          confirmLabel="Remove"
          onConfirm={() => void confirmDelete()}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </Section>
  );
}

// ── activity ─────────────────────────────────────────────────────────────────

const ActivityList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActivityRow = styled.li`
  display: flex;
  align-items: baseline;
  column-gap: 0.9em;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ActivityWho = styled.strong`
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: 700;
`;

/** Humanizes an activity detail's raw enum value ("in-progress") via the
 * existing label maps ("In progress") — falls back to the raw value for
 * anything the map doesn't recognize, and "—" for null/undefined, rather than
 * ever printing "undefined" (2026-08-10 adversarial review, item 18). */
function humanizeEnum<T extends string>(labels: Record<T, string>, value: unknown): string {
  if (value == null) return 'None';
  const key = String(value) as T;
  return labels[key] ?? String(value);
}

function activitySentence(verb: string, detail: Record<string, unknown>): string {
  switch (verb) {
    case 'created':
      return 'reported this';
    case 'status_changed':
      return `changed status from ${humanizeEnum(FEEDBACK_STATUS_LABELS, detail.old)} to ${humanizeEnum(FEEDBACK_STATUS_LABELS, detail.new)}`;
    case 'priority_changed':
      return `changed priority from ${humanizeEnum(FEEDBACK_PRIORITY_LABELS, detail.old)} to ${humanizeEnum(FEEDBACK_PRIORITY_LABELS, detail.new)}`;
    case 'assigned':
      return detail.new ? 'assigned this item' : 'unassigned this item';
    case 'pinned':
      return 'pinned this item';
    case 'unpinned':
      return 'unpinned this item';
    case 'stage_added':
      return `added stage "${detail.title ?? ''}"`;
    case 'stage_updated':
      return `updated stage "${detail.title ?? ''}"`;
    case 'stage_completed':
      return `completed stage "${detail.title ?? ''}"`;
    case 'stage_removed':
      return `removed stage "${detail.title ?? ''}"`;
    case 'stage_reordered':
      return 'reordered the roadmap stages';
    case 'roadmap_updated':
      return 'updated the roadmap';
    case 'relation_added':
      return `linked related item ${detail.to ?? ''}`;
    case 'relation_removed':
      return `unlinked related item ${detail.to ?? ''}`;
    case 'fields_updated':
      return 'updated tracker fields';
    default:
      return verb.replace(/_/g, ' ');
  }
}

// ── comments ─────────────────────────────────────────────────────────────────

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CommentRow = styled.div<{ $internal: boolean }>`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $internal }) => ($internal ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $internal }) => {
    const base = theme.cardsGlass ? 'transparent' : theme.color.surface;
    return $internal ? `color-mix(in srgb, ${theme.color.accentAmber} 14%, ${base})` : base;
  }};
`;

const CommentMain = styled.div`
  min-width: 0;
  flex: 1;
`;

const CommentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const PillTag = styled.span<{ $tone: 'staff' | 'internal' }>`
  padding: 1px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-weight: 700;
  color: ${({ theme, $tone }) =>
    $tone === 'internal' ? theme.color.accentAmber : theme.color.brandFrom};
  background: ${({ theme, $tone }) =>
    $tone === 'internal' ? `${theme.color.accentAmber}22` : `${theme.color.brandFrom}22`};
  border: 1px solid transparent;
`;

const CommentBody = styled.p`
  margin: 0 0 6px;
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.5;
`;

const HelpfulButton = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px;
  cursor: pointer;
  border: none;
  background: transparent;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textMuted)};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const InternalToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ── side rail: Status card ──────────────────────────────────────────────────

const RailSelect = styled.select`
  width: 100%;
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const RailNote = styled.p`
  margin: 8px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.4;
`;

const PublicUpdateBanner = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => `${theme.color.brandFrom}22`};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

function StatusCard({
  status,
  statusNote,
  isStaff,
  onSetStatus,
}: {
  status: FeedbackStatus;
  statusNote: string;
  isStaff: boolean;
  onSetStatus: (s: FeedbackStatus) => void;
}) {
  return (
    <Panel data-testid="feedback-status-card">
      <SectionLabel as="h2">Status</SectionLabel>
      <StatusBadge status={status} />
      {statusNote && <RailNote>{statusNote}</RailNote>}
      {isStaff && (
        <RailSelect
          aria-label="Set status"
          value={status}
          onChange={(e) => onSetStatus(e.target.value as FeedbackStatus)}
        >
          {FEEDBACK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {FEEDBACK_STATUS_LABELS[s]}
            </option>
          ))}
        </RailSelect>
      )}
    </Panel>
  );
}

// ── side rail: Roadmap & Progress card ──────────────────────────────────────

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  margin-top: 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.brandFrom},
    ${({ theme }) => theme.color.brandTo}
  );
`;

const ProgressPctLabel = styled.span`
  display: block;
  margin-top: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MilestoneRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const StageList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StageRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StageDot = styled.span<{ $state: FeedbackStageState }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $state }) => ($state === 'pending' ? theme.color.glassBorder : 'transparent')};
  background: ${({ theme, $state }) =>
    $state === 'done'
      ? theme.color.success
      : $state === 'in_progress'
        ? `${theme.color.brandFrom}2e`
        : 'transparent'};
  color: ${({ theme }) => theme.color.onAccent};
`;

const StageText = styled.div`
  flex: 1;
  min-width: 0;
`;

const StageTitle = styled.span<{ $done: boolean }>`
  display: block;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
  opacity: ${({ $done }) => ($done ? 0.7 : 1)};
`;

const StageSub = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const StageMiniTrack = styled.div`
  width: 100%;
  height: 4px;
  margin-top: 3px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
`;

const StageMiniFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme.color.brandFrom};
`;

const StageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const RoadmapEditBlock = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CharCount = styled.span<{ $over: boolean }>`
  display: block;
  margin-top: 2px;
  text-align: right;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme, $over }) => ($over ? theme.color.danger : theme.color.textMuted)};
`;

const StageEditRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

function stageStateLabel(s: FeedbackStageState): string {
  return s === 'done' ? 'Done' : s === 'in_progress' ? 'In progress' : 'Pending';
}

function RoadmapCard({
  item,
  canManage,
  isStaff,
  onSetRoadmap,
  onAddStage,
  onUpdateStage,
  onDeleteStage,
  onReorderStages,
}: {
  item: {
    roadmap: {
      milestoneLabel: string;
      milestoneEta: string | null;
      publicUpdate: string;
      overallProgress: number;
      stages: FeedbackStage[];
    };
  };
  canManage: boolean;
  isStaff: boolean;
  onSetRoadmap: (input: {
    milestone_label?: string;
    milestone_eta?: string | null;
    public_update?: string;
  }) => Promise<void>;
  onAddStage: (title: string) => Promise<void>;
  onUpdateStage: (id: number, input: Record<string, unknown>) => Promise<void>;
  onDeleteStage: (id: number) => Promise<{ refreshed: boolean }>;
  onReorderStages: (order: number[]) => Promise<void>;
}) {
  const toast = useToast();
  const { roadmap } = item;
  const [editing, setEditing] = useState(false);
  const [milestoneLabel, setMilestoneLabel] = useState(roadmap.milestoneLabel);
  const [milestoneEta, setMilestoneEta] = useState(roadmap.milestoneEta ?? '');
  const [publicUpdate, setPublicUpdate] = useState(roadmap.publicUpdate);
  const [newStageTitle, setNewStageTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDeleteStage, setPendingDeleteStage] = useState<FeedbackStage | null>(null);

  function beginEdit() {
    setMilestoneLabel(roadmap.milestoneLabel);
    setMilestoneEta(roadmap.milestoneEta ?? '');
    setPublicUpdate(roadmap.publicUpdate);
    setEditing(true);
  }

  async function saveRoadmap() {
    if (publicUpdate.length > 300 || saving) return;
    setSaving(true);
    try {
      // Roadmap block PATCHes as one batch — milestone label/ETA/public update
      // share one endpoint (POST /roadmap/), unlike stages below, which have
      // their own per-row endpoints and save immediately (documented judgment
      // call: matching each mutation to the shape of its actual endpoint).
      await onSetRoadmap({
        milestone_label: milestoneLabel,
        milestone_eta: milestoneEta || null,
        public_update: publicUpdate,
      });
      setEditing(false);
      toast('Roadmap updated.');
    } catch (err) {
      toast(describeManageError(err, 'Could not save the roadmap. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function addStage() {
    const title = newStageTitle.trim();
    if (!title || saving) return;
    setSaving(true);
    try {
      await onAddStage(title);
      setNewStageTitle('');
    } catch (err) {
      toast(describeManageError(err, 'Could not add that stage.'));
    } finally {
      setSaving(false);
    }
  }

  async function cycleState(stage: FeedbackStage) {
    const next: FeedbackStageState =
      stage.state === 'pending'
        ? 'in_progress'
        : stage.state === 'in_progress'
          ? 'done'
          : 'pending';
    try {
      await onUpdateStage(stage.id, { state: next });
    } catch (err) {
      toast(describeManageError(err, 'Could not update that stage.'));
    }
  }

  async function toggleLock(stage: FeedbackStage) {
    try {
      await onUpdateStage(stage.id, { locked: !stage.locked });
    } catch (err) {
      toast(describeManageError(err, 'Could not change that stage lock.'));
    }
  }

  async function move(stage: FeedbackStage, dir: -1 | 1) {
    const order = [...roadmap.stages].sort((a, b) => a.position - b.position).map((s) => s.id);
    const idx = order.indexOf(stage.id);
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= order.length) return;
    [order[idx], order[swapWith]] = [order[swapWith]!, order[idx]!];
    try {
      await onReorderStages(order);
    } catch (err) {
      toast(describeManageError(err, 'Could not reorder stages.'));
    }
  }

  async function confirmDeleteStage() {
    if (!pendingDeleteStage) return;
    const s = pendingDeleteStage;
    setPendingDeleteStage(null);
    try {
      const { refreshed } = await onDeleteStage(s.id);
      if (!refreshed) {
        toast('Stage removed, but the page may be out of date. Refresh to confirm.');
      }
    } catch (err) {
      toast(describeManageError(err, 'Could not remove that stage.'));
    }
  }

  const sortedStages = [...roadmap.stages].sort((a, b) => a.position - b.position);

  return (
    <Panel data-testid="feedback-roadmap-card">
      <SectionLabel as="h2">Roadmap &amp; progress</SectionLabel>

      {(roadmap.milestoneLabel || roadmap.milestoneEta) && !editing && (
        <MilestoneRow>
          <span>{roadmap.milestoneLabel || 'Milestone'}</span>
          {roadmap.milestoneEta && <span>{roadmap.milestoneEta}</span>}
        </MilestoneRow>
      )}

      <ProgressTrack>
        <ProgressFill $pct={roadmap.overallProgress} />
      </ProgressTrack>
      <ProgressPctLabel>{roadmap.overallProgress}% overall</ProgressPctLabel>

      {sortedStages.length === 0 ? (
        <RailNote>No roadmap stages yet.</RailNote>
      ) : (
        <StageList>
          {sortedStages.map((s, i) => (
            <StageRow key={s.id}>
              <StageDot $state={s.state}>
                {s.state === 'done' && <Icon name="check" size={10} />}
              </StageDot>
              <StageText>
                <StageTitle $done={s.state === 'done'}>
                  {s.title} {s.locked && <Icon name="lock" size={10} />}
                </StageTitle>
                {s.state === 'done' && s.completedAt && (
                  <StageSub>Completed {s.completedAt}</StageSub>
                )}
                {s.state === 'in_progress' && (
                  <>
                    <StageSub>In progress</StageSub>
                    <StageMiniTrack>
                      <StageMiniFill $pct={s.progress_pct} />
                    </StageMiniTrack>
                  </>
                )}
                {s.state === 'pending' && s.eta && <StageSub>ETA {s.eta}</StageSub>}
              </StageText>
              {canManage && (!s.locked || isStaff) && (
                <StageActions>
                  <IconButton
                    icon="arrow-up"
                    label={`Move "${s.title}" up`}
                    size={12}
                    onClick={() => void move(s, -1)}
                    disabled={i === 0}
                  />
                  <IconButton
                    icon="arrow-down"
                    label={`Move "${s.title}" down`}
                    size={12}
                    onClick={() => void move(s, 1)}
                    disabled={i === sortedStages.length - 1}
                  />
                  <IconButton
                    icon="check"
                    label={`Advance "${s.title}" to ${stageStateLabel(
                      s.state === 'pending'
                        ? 'in_progress'
                        : s.state === 'in_progress'
                          ? 'done'
                          : 'pending',
                    )}`}
                    size={12}
                    onClick={() => void cycleState(s)}
                  />
                  {isStaff && (
                    <IconButton
                      icon="lock"
                      label={`Toggle lock on "${s.title}"`}
                      size={12}
                      onClick={() => void toggleLock(s)}
                    />
                  )}
                  <IconButton
                    icon="trash"
                    label={`Remove "${s.title}"`}
                    size={12}
                    onClick={() => setPendingDeleteStage(s)}
                  />
                </StageActions>
              )}
            </StageRow>
          ))}
        </StageList>
      )}

      {canManage && (
        <RoadmapEditBlock>
          {!editing ? (
            <Button variant="ghost" onClick={beginEdit}>
              Edit roadmap
            </Button>
          ) : (
            <>
              <TextField
                label="Milestone label"
                value={milestoneLabel}
                maxLength={120}
                onChange={(e) => setMilestoneLabel(e.target.value)}
              />
              <TextField
                label="Milestone ETA"
                type="date"
                value={milestoneEta}
                onChange={(e) => setMilestoneEta(e.target.value)}
              />
              <Textarea
                label="Public update"
                value={publicUpdate}
                maxLength={300}
                rows={3}
                onChange={(e) => setPublicUpdate(e.target.value)}
              />
              <CharCount $over={publicUpdate.length > 300}>
                {publicUpdate.length}/300, visible to everyone
              </CharCount>
              <Button variant="primary" onClick={() => void saveRoadmap()} disabled={saving}>
                {saving ? 'Saving…' : 'Save roadmap'}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          )}

          <StageEditRow>
            <TextField
              label="New stage"
              value={newStageTitle}
              maxLength={120}
              onChange={(e) => setNewStageTitle(e.target.value)}
              placeholder="e.g. Backend review"
            />
            <Button
              variant="ghost"
              onClick={() => void addStage()}
              disabled={!newStageTitle.trim() || saving}
            >
              Add
            </Button>
          </StageEditRow>
        </RoadmapEditBlock>
      )}

      {pendingDeleteStage && (
        <ConfirmDialog
          title="Remove stage"
          message={`Remove the stage "${pendingDeleteStage.title}"? This can't be undone.`}
          confirmLabel="Remove"
          onConfirm={() => void confirmDeleteStage()}
          onClose={() => setPendingDeleteStage(null)}
        />
      )}
    </Panel>
  );
}

// ── side rail: Details card ─────────────────────────────────────────────────

const DetailsGrid = styled.dl`
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DetailsTerm = styled.dt`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const DetailsValue = styled.dd`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const WatcherAvatars = styled.span`
  display: inline-flex;

  > * + * {
    margin-left: -8px;
  }
`;

const AvatarRing = styled.span`
  display: inline-flex;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 2px solid ${({ theme }) => theme.color.bgRoot};
`;

function DetailsCard({
  priority,
  category,
  assignedToHandle,
  watcherCount,
  watcherAvatars,
  canManage,
  isStaff,
  onSetPriority,
  onSetAssignee,
  pinned,
  onTogglePin,
}: {
  priority: FeedbackPriority;
  category: string;
  assignedToHandle: string;
  watcherCount: number;
  watcherAvatars: { name: string; av: string }[];
  canManage: boolean;
  isStaff: boolean;
  onSetPriority: (p: FeedbackPriority) => void;
  onSetAssignee: (handle: string) => void;
  pinned: boolean;
  onTogglePin: () => void;
}) {
  const [assignDraft, setAssignDraft] = useState(assignedToHandle);

  // Re-seed after a successful assign (assignedToHandle changes once the
  // mutation resolves and the item refetches) — otherwise the input keeps
  // showing what was typed even after the save actually landed
  // (2026-08-10 adversarial review, item 18).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing an editable draft to its prop source, not a data fetch
    setAssignDraft(assignedToHandle);
  }, [assignedToHandle]);

  return (
    <Panel data-testid="feedback-details-card">
      <SectionLabel as="h2">Details</SectionLabel>
      <DetailsGrid>
        <DetailsRow>
          <DetailsTerm>Priority</DetailsTerm>
          {canManage ? (
            <RailSelect
              aria-label="Set priority"
              value={priority}
              onChange={(e) => onSetPriority(e.target.value as FeedbackPriority)}
              style={{ marginTop: 0, width: 'auto' }}
            >
              {FEEDBACK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {FEEDBACK_PRIORITY_LABELS[p]}
                </option>
              ))}
            </RailSelect>
          ) : (
            <DetailsValue>{FEEDBACK_PRIORITY_LABELS[priority]}</DetailsValue>
          )}
        </DetailsRow>
        <DetailsRow>
          <DetailsTerm>Category</DetailsTerm>
          <DetailsValue>{category}</DetailsValue>
        </DetailsRow>
        <DetailsRow>
          <DetailsTerm>Assigned to</DetailsTerm>
          <DetailsValue>{assignedToHandle || 'Unassigned'}</DetailsValue>
        </DetailsRow>
        {isStaff && (
          <StageEditRow>
            <TextField
              label="Assign (@handle, blank to unassign)"
              value={assignDraft}
              onChange={(e) => setAssignDraft(e.target.value)}
            />
            <Button variant="ghost" onClick={() => onSetAssignee(assignDraft)}>
              Set
            </Button>
          </StageEditRow>
        )}
        <DetailsRow>
          <DetailsTerm>Following</DetailsTerm>
          <DetailsValue>
            {watcherCount}
            {watcherAvatars.length > 0 && (
              <WatcherAvatars aria-hidden="true">
                {watcherAvatars.slice(0, 5).map((w, i) => (
                  <AvatarRing key={i}>
                    <Avatar name={w.name} avatarKey={w.av} size={20} />
                  </AvatarRing>
                ))}
              </WatcherAvatars>
            )}
          </DetailsValue>
        </DetailsRow>
        {isStaff && (
          <DetailsRow>
            <DetailsTerm>Pinned</DetailsTerm>
            <Switch checked={pinned} onChange={onTogglePin} label="Pin this item" />
          </DetailsRow>
        )}
      </DetailsGrid>
    </Panel>
  );
}

// ── side rail: Related feedback card ────────────────────────────────────────

const RelatedList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RelatedRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const RelatedLink = styled.a`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function RelatedCard({
  related,
  canManage,
  onAdd,
  onRemove,
}: {
  related: { id: string; title: string; status: FeedbackStatus }[];
  canManage: boolean;
  onAdd: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<{ refreshed: boolean }>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    const id = parseFeedbackIdInput(draft);
    if (!id || busy) return;
    setBusy(true);
    try {
      await onAdd(id);
      setDraft('');
    } catch (err) {
      toast(describeManageError(err, 'Could not link that item. Check the id.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      const { refreshed } = await onRemove(id);
      if (!refreshed) {
        toast('Unlinked, but the page may be out of date. Refresh to confirm.');
      }
    } catch (err) {
      toast(describeManageError(err, 'Could not unlink that item.'));
    }
  }

  if (!canManage && related.length === 0) return null;

  return (
    <Panel data-testid="feedback-related-card">
      <SectionLabel as="h2">Related feedback</SectionLabel>
      {related.length === 0 ? (
        <RailNote>No related items.</RailNote>
      ) : (
        <RelatedList>
          {related.map((r) => (
            <RelatedRow key={r.id}>
              <RelatedLink href={`/feedback/${r.id}`}>{r.title}</RelatedLink>
              {canManage && (
                <IconButton
                  icon="close"
                  label={`Unlink ${r.title}`}
                  size={12}
                  onClick={() => void remove(r.id)}
                />
              )}
            </RelatedRow>
          ))}
        </RelatedList>
      )}
      {canManage && (
        <StageEditRow>
          <TextField
            label="Link by id (e.g. fb-42 or #FB-2026-00042)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button variant="ghost" onClick={() => void add()} disabled={busy || !draft.trim()}>
            Link
          </Button>
        </StageEditRow>
      )}
    </Panel>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export function FeedbackDetailPage() {
  const { id } = useParams();
  const {
    item,
    loading,
    notFound,
    error,
    reload,
    toggleUpvote,
    toggleWatch,
    comment,
    toggleCommentHelpful,
    setStatus,
    manage,
    setRoadmap,
    addStage,
    updateStage,
    deleteStage,
    reorderStages,
    addRelation,
    removeRelation,
    uploadAttachment,
    removeAttachment,
    addTag,
    removeTag,
  } = useFeedbackItem(id);
  const { isAuthenticated, currentUser } = useSession();
  const requireAuth = useRequireAuth();
  const toast = useToast();

  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewPublic, setPreviewPublic] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Raw viewer facts. "Preview public view" simulates exactly what a normal
  // visitor sees, so EVERY affordance gated on manage/staff/reporter status
  // must read the `effective*` values below, never these raw ones directly
  // (2026-08-10 adversarial review, item 4) — including "you're the
  // reporter" affordances like the attachment upload button, which a real
  // stranger would never see either.
  const isStaff = !!currentUser?.isAdmin;
  const myHandle = currentUser?.handle;
  const isReporter = !!item && myHandle === item.author.handle;

  const effectiveCanManage = !previewPublic && !!item?.canManage;
  const effectiveIsStaff = isStaff && !previewPublic;
  const effectiveIsReporter = isReporter && !previewPublic;
  const canUploadAttachment = effectiveCanManage || effectiveIsReporter;

  const rail = useMemo(
    () => (
      <RailGroup pageKey="feedback-detail">
        {item ? (
          <>
            <RailPanel id="status" label="Status" required>
              <StatusCard
                status={item.status}
                statusNote={item.statusNote}
                isStaff={effectiveIsStaff}
                onSetStatus={(s) =>
                  setStatus(s).catch((err) =>
                    toast(describeManageError(err, 'Could not update status. Please try again.')),
                  )
                }
              />
            </RailPanel>
            <RailPanel id="roadmap" label="Roadmap" required>
              <RoadmapCard
                item={item}
                canManage={effectiveCanManage}
                isStaff={effectiveIsStaff}
                onSetRoadmap={setRoadmap}
                onAddStage={addStage}
                onUpdateStage={updateStage}
                onDeleteStage={deleteStage}
                onReorderStages={reorderStages}
              />
            </RailPanel>
            <RailPanel id="details" label="Details" required>
              <DetailsCard
                priority={item.priority}
                category={FEEDBACK_CATEGORY_LABELS[item.category]}
                assignedToHandle={item.assignedTo?.handle ?? ''}
                watcherCount={item.watchers.count}
                watcherAvatars={item.watchers.avatars}
                canManage={effectiveCanManage}
                isStaff={effectiveIsStaff}
                onSetPriority={(p) =>
                  manage({ priority: p }).catch((err) =>
                    toast(describeManageError(err, 'Could not update priority. Please try again.')),
                  )
                }
                onSetAssignee={(handle) =>
                  manage({ assigned_to: handle }).catch((err) =>
                    toast(
                      describeManageError(err, 'Could not update the assignee. Please try again.'),
                    ),
                  )
                }
                pinned={item.pinned}
                onTogglePin={() =>
                  manage({ pinned: !item.pinned }).catch((err) =>
                    toast(describeManageError(err, 'Could not update pin. Please try again.')),
                  )
                }
              />
            </RailPanel>
            <RailPanel id="related" label="Related feedback" required={effectiveCanManage}>
              <RelatedCard
                related={item.related}
                canManage={effectiveCanManage}
                onAdd={addRelation}
                onRemove={removeRelation}
              />
            </RailPanel>
          </>
        ) : (
          <RailPanel id="how-it-works" label="How it works">
            <FeedbackHowItWorksPanel />
          </RailPanel>
        )}
      </RailGroup>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item, effectiveIsStaff, effectiveCanManage],
  );
  useRightRail(rail);

  // Hold the frame while the by-id lookup is in flight — rendering
  // FeedbackNotFound here would flash it for every real item.
  if (loading) return null;
  if (notFound) return <FeedbackNotFound />;
  if (error || !item) {
    return <FeedbackLoadError onRetry={() => void reload()} />;
  }

  function handleUpvote() {
    if (!requireAuth('upvote feedback')) return;
    // Optimistic flip already happened inside toggleUpvote; on failure the
    // store has already rolled back — this just surfaces the failure honestly.
    toggleUpvote().catch(() => toast('Could not update your vote. Please try again.'));
  }

  function handleWatch() {
    if (!requireAuth('follow feedback')) return;
    toggleWatch().catch(() => toast('Could not update your follow. Please try again.'));
  }

  async function handleComment() {
    if (!requireAuth('reply')) return;
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await comment(body, internal && effectiveIsStaff);
      setDraft('');
      setInternal(false);
    } catch {
      toast('Could not post that reply. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleHelpful(commentId: string) {
    if (!requireAuth('mark helpful')) return;
    try {
      await toggleCommentHelpful(commentId);
    } catch (err) {
      if (err instanceof ApiError) {
        // The backend returns 404 for BOTH "no such comment" and "internal
        // note, non-staff viewer" — deliberately indistinguishable (see
        // api.py toggle_comment_helpful) so the response itself can't leak
        // that an internal note exists. Silently ignore either case; a real
        // 400 (some other validation) still gets a real error toast.
        if (err.status === 404) return;
        if (err.status === 400) {
          toast('Could not update that comment.');
          return;
        }
      }
      toast('Could not update that. Please try again.');
    }
  }

  async function handleAddTag() {
    const label = newTag.trim();
    if (!label) return;
    try {
      await addTag(label);
      setAddingTag(false);
      setNewTag('');
    } catch (err) {
      toast(describeManageError(err, 'Could not add that tag. Please try again.'));
    }
  }

  async function handleRemoveTag(label: string) {
    try {
      const { refreshed } = await removeTag(label);
      if (!refreshed) {
        toast('Tag removed, but the page may be out of date. Refresh to confirm.');
      }
    } catch (err) {
      toast(describeManageError(err, 'Could not remove that tag. Please try again.'));
    }
  }

  const details = item.details ?? {};
  const topicFields = FEEDBACK_TOPIC_FIELDS[item.category] ?? [];
  // 'expected'/'actual'/'steps' can come from either the pre-1.21.50
  // per-category `details` JSON (legacy items) OR the new top-level columns
  // (items submitted through SubmitFeedbackPanel) — the legacy answer wins
  // when BOTH somehow exist (should never happen in practice: the new panel
  // never writes `details`), so a page never shows the same content twice.
  const hasLegacyExpectedActual =
    Object.hasOwn(details, 'expected') && Object.hasOwn(details, 'actual');
  const hasExpectedActual =
    hasLegacyExpectedActual || !!(item.expectedBehavior || item.actualBehavior);
  const expectedText = hasLegacyExpectedActual ? details.expected : item.expectedBehavior;
  const actualText = hasLegacyExpectedActual ? details.actual : item.actualBehavior;
  const stepsRaw = details.steps || item.stepsToReproduce;
  const stepsLines = stepsRaw ? stepsRaw.split(/\r?\n/).filter((l) => l.trim()) : [];
  // Only pull 'expected'/'actual' out of the generic grid when BOTH are
  // present (the two-column block below covers that case) — a lone
  // 'expected' answer (no 'actual' yet) still needs to render somewhere.
  // Same idea for 'steps': only special-cased once it actually produced a list.
  const metaFields = topicFields.filter((f) => {
    if (!Object.hasOwn(details, f.id)) return false;
    if (hasLegacyExpectedActual && (f.id === 'expected' || f.id === 'actual')) return false;
    if (stepsLines.length > 0 && f.id === 'steps') return false;
    return true;
  });

  // New (1.21.50) always-public metadata row: parsed device/browser/OS,
  // submitted timezone, and any standard-browser-API snapshot fields present
  // — distinct from `metaFields` above (the legacy per-category, sometimes
  // staff_only, `details` answers).
  const deviceMetaEntries: { label: string; value: string }[] = [];
  if (item.platform !== 'unspecified') {
    deviceMetaEntries.push({
      label: 'Reported on',
      value: FEEDBACK_PLATFORM_LABELS[item.platform],
    });
  }
  if (item.devicePlatform) {
    deviceMetaEntries.push({ label: 'Device platform', value: item.devicePlatform });
  }
  if (item.browser) deviceMetaEntries.push({ label: 'Browser', value: item.browser });
  if (item.osVersion) deviceMetaEntries.push({ label: 'OS version', value: item.osVersion });
  if (item.submittedTz)
    deviceMetaEntries.push({ label: 'Submitted from', value: item.submittedTz });
  for (const key of DEVICE_SNAPSHOT_FIELD_ORDER) {
    const value = item.deviceSnapshot?.[key];
    if (value !== undefined) {
      deviceMetaEntries.push({ label: DEVICE_SNAPSHOT_FIELD_LABELS[key], value: String(value) });
    }
  }

  return (
    <section>
      {!isAuthenticated && (
        <GuestBanner message="You're viewing feedback on TruXSocial. Sign in to upvote, follow or reply." />
      )}

      {item.publicUpdate && (
        <PublicUpdateBanner role="status">
          <Icon name="megaphone" size={16} /> {item.publicUpdate}
        </PublicUpdateBanner>
      )}

      {item.canManage && (
        <RailNote style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch checked={previewPublic} onChange={setPreviewPublic} label="Preview public view" />
          Preview public view
        </RailNote>
      )}

      <Card>
        <Head>
          <Upvote
            type="button"
            $on={item.upvoted}
            aria-pressed={item.upvoted}
            aria-label={`Upvote (${item.upvotes})`}
            onClick={handleUpvote}
          >
            <Caret>
              <Icon name="chevron-down" size={16} />
            </Caret>
            <Count>{item.upvotes}</Count>
          </Upvote>

          <HeadMain>
            <HeadTop>
              <div>
                <CategoryChip>
                  <Icon name={CATEGORY_ICONS[item.category]} size={12} />
                  {FEEDBACK_CATEGORY_LABELS[item.category]}
                </CategoryChip>
                <Title>{item.title}</Title>
                <Meta>
                  <Chip>
                    <Avatar name={item.author.name} avatarKey={item.author.av} size={18} />
                    {item.author.handle}
                  </Chip>
                  <span>Posted {relativeTime(item.createdAt)}</span>
                  <span>{displayFeedbackId(item.id, item.createdAt)}</span>
                  <Chip>
                    <Icon name="eye" size={12} />
                    {item.views ?? 0} {(item.views ?? 0) === 1 ? 'view' : 'views'}
                  </Chip>
                  <StatusBadge status={item.status} />
                </Meta>
              </div>
              <WatchButton
                type="button"
                $on={item.watchers.watching}
                aria-pressed={item.watchers.watching}
                onClick={handleWatch}
              >
                <Icon name={item.watchers.watching ? 'check' : 'plus'} size={13} />
                {item.watchers.watching ? 'Following' : 'Follow'}
              </WatchButton>
            </HeadTop>

            <TagsRow>
              {item.productArea && <Tag>{item.productArea}</Tag>}
              {/* Public whenever staff have set a priority at all (not just
                  high/critical) — still staff-set only, per task spec §6. */}
              {item.priority !== 'none' && (
                <Tag
                  $tone={
                    item.priority === 'high' || item.priority === 'critical' ? 'danger' : undefined
                  }
                >
                  {FEEDBACK_PRIORITY_LABELS[item.priority]} priority
                </Tag>
              )}
              {item.removed && <Tag $tone="danger">Hidden</Tag>}
              {item.tags.map((t) => (
                <Tag key={t}>
                  {t}
                  {effectiveCanManage && (
                    <TagRemoveButton
                      type="button"
                      aria-label={`Remove tag ${t}`}
                      onClick={() => void handleRemoveTag(t)}
                    >
                      <Icon name="close" size={11} />
                    </TagRemoveButton>
                  )}
                </Tag>
              ))}
              {effectiveCanManage &&
                item.tags.length < MAX_TAGS_PER_ITEM &&
                (addingTag ? (
                  <AddTagRow>
                    <AddTagInput
                      autoFocus
                      aria-label="New tag"
                      maxLength={TAG_MAX_LEN}
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleAddTag();
                        if (e.key === 'Escape') {
                          setAddingTag(false);
                          setNewTag('');
                        }
                      }}
                    />
                    <IconButton
                      icon="check"
                      label="Save tag"
                      size={13}
                      onClick={() => void handleAddTag()}
                    />
                    <IconButton
                      icon="close"
                      label="Cancel"
                      size={13}
                      onClick={() => {
                        setAddingTag(false);
                        setNewTag('');
                      }}
                    />
                  </AddTagRow>
                ) : (
                  <AddTagButton type="button" onClick={() => setAddingTag(true)}>
                    <Icon name="plus" size={11} /> Add tag
                  </AddTagButton>
                ))}
            </TagsRow>
          </HeadMain>
        </Head>

        <DescriptionBlock>
          <FeedbackMarkdown source={item.body} />
        </DescriptionBlock>

        {item.additionalContext && (
          <ContextBlock>
            <InlineLabel>Additional context</InlineLabel>
            <EAText>{item.additionalContext}</EAText>
          </ContextBlock>
        )}

        {stepsLines.length > 0 && (
          <ContextBlock>
            <InlineLabel>Steps to reproduce</InlineLabel>
            <StepsList>
              {stepsLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </StepsList>
          </ContextBlock>
        )}

        {hasExpectedActual && (
          <ExpectedActual>
            <EAColumn $tone="expected">
              <EAHead $tone="expected">
                <Icon name="check" size={13} /> Expected
              </EAHead>
              <EAText>{expectedText}</EAText>
            </EAColumn>
            <EAColumn $tone="actual">
              <EAHead $tone="actual">
                <Icon name="close" size={13} /> Actual
              </EAHead>
              <EAText>{actualText}</EAText>
            </EAColumn>
          </ExpectedActual>
        )}

        {deviceMetaEntries.length > 0 && (
          <MetaGrid>
            {deviceMetaEntries.map((entry) => (
              <MetaRow key={entry.label}>
                <MetaTerm>{entry.label}</MetaTerm>
                <MetaValue>{entry.value}</MetaValue>
              </MetaRow>
            ))}
          </MetaGrid>
        )}

        {metaFields.length > 0 && (
          <MetaGrid>
            {metaFields.map((f) => (
              <MetaRow key={f.id}>
                <MetaTerm>
                  {f.label}
                  {f.staffOnly && <Tag $tone="danger">Staff only</Tag>}
                </MetaTerm>
                <MetaValue>{fieldValueLabel(item.category, f.id, details[f.id]!)}</MetaValue>
              </MetaRow>
            ))}
          </MetaGrid>
        )}
      </Card>

      <AttachmentsSection
        attachments={item.attachments}
        canUpload={canUploadAttachment}
        canDeleteAll={effectiveIsStaff}
        myHandle={previewPublic ? undefined : myHandle}
        onUpload={uploadAttachment}
        onRemove={removeAttachment}
      />

      {item.activity.length > 0 && (
        <Section>
          <SubHead>Activity</SubHead>
          <ActivityList>
            {item.activity.map((a) => (
              <ActivityRow key={a.id}>
                <span>
                  <ActivityWho>{a.actor?.name ?? 'System'}</ActivityWho>{' '}
                  {activitySentence(a.verb, a.detail)}
                </span>
                <span>{relativeTime(a.createdAt)}</span>
              </ActivityRow>
            ))}
          </ActivityList>
        </Section>
      )}

      <Section>
        <SubHead>
          {item.commentCount} {item.commentCount === 1 ? 'reply' : 'replies'}
        </SubHead>

        {item.comments.length === 0 ? (
          <Empty>No replies yet.</Empty>
        ) : (
          <CommentList>
            {item.comments.map((c) => (
              <CommentRow key={c.id} $internal={c.internal} data-testid={`comment-${c.id}`}>
                <Avatar name={c.author.name} avatarKey={c.author.av} size={30} />
                <CommentMain>
                  <CommentMeta>
                    <strong>{c.author.name}</strong>
                    <span>{c.author.handle}</span>
                    {c.author.isStaff && <PillTag $tone="staff">Team</PillTag>}
                    {c.internal && <PillTag $tone="internal">Internal note</PillTag>}
                    <span>{relativeTime(c.createdAt)}</span>
                  </CommentMeta>
                  <CommentBody as="div">
                    <FeedbackMarkdown source={c.body} />
                  </CommentBody>
                  {!c.internal && (
                    <HelpfulButton
                      type="button"
                      $on={c.helpful}
                      aria-pressed={c.helpful}
                      onClick={() => void handleHelpful(c.id)}
                    >
                      <Icon name="check" size={12} /> Helpful
                      {c.helpfulCount > 0 ? ` (${c.helpfulCount})` : ''}
                    </HelpfulButton>
                  )}
                </CommentMain>
              </CommentRow>
            ))}
          </CommentList>
        )}

        <Composer>
          <MarkdownEditor
            label="Add a reply"
            value={draft}
            onChange={setDraft}
            placeholder={isAuthenticated ? 'Add a reply…' : 'Sign in to reply'}
            rows={3}
            lightToolbar
            disabled={!isAuthenticated}
          />
          <ComposerActions>
            {effectiveIsStaff ? (
              <InternalToggle>
                <input
                  type="checkbox"
                  checked={internal}
                  onChange={(e) => setInternal(e.target.checked)}
                />
                Internal note: visible to staff only
              </InternalToggle>
            ) : (
              <span />
            )}
            <Button
              variant="primary"
              onClick={() => void handleComment()}
              disabled={busy || !draft.trim()}
            >
              {busy ? 'Posting…' : 'Reply'}
            </Button>
          </ComposerActions>
        </Composer>
      </Section>
    </section>
  );
}
